import type { AxiosError } from 'axios';

import AlertStore from 'jsx/stores/alertStore';
import Config from 'jsx/constants/config';
import httpStatusCodes from 'jsx/constants/httpStatusCodes';

import { isErrorResponse, isValidationErrorResponse } from '../errors';
import { AxiosBuilderInterceptor } from './types';

/**
 * The statuses this interceptor deliberately stays silent on. Suppressed is NOT
 * the same as unhandled, and a caller reading this as "nobody tells the patient
 * anything" will get two of the four wrong:
 *
 * - `forbidden` (403) is silent because `authErrorHandler` runs first and
 *   navigates to the login page; the point is that nothing flashes before it.
 * - `csrfMismatch` (419) is silent because `csrfRetryHandler` runs first and
 *   refreshes the token; only an exhausted retry gets this far, unannounced.
 * - `locked` (423) and `tooManyAttempts` (429) are silent with nobody else
 *   acting, so a caller that renders its own message is the patient's only
 *   signal — but neither is fixed by retrying, so the copy must not say so.
 *
 * Exported for callers that render their own failure message, so they can ask
 * this instead of re-listing the statuses and drifting from it.
 */
export const shouldSuppressAlert = (error: AxiosError<unknown>): boolean => {
  if (
    error.response?.status === httpStatusCodes.locked ||
    error.response?.status === httpStatusCodes.forbidden ||
    error.response?.status === httpStatusCodes.tooManyAttempts ||
    error.response?.status === httpStatusCodes.csrfMismatch
  ) {
    return true;
  }
  return false;
};

const handleAxiosError = (error: AxiosError<unknown>): void => {
  if (shouldSuppressAlert(error)) return;

  if (!error.response || !isErrorResponse(error.response.data)) {
    AlertStore.add(
      error.message === 'Network Error'
        ? Config.errorMessages.network
        : Config.errorMessages.generic,
    );
    return;
  }

  const { data } = error.response;

  if (!isValidationErrorResponse(data)) {
    AlertStore.add(data.message);
    return;
  }

  Object.values(data.errors)
    .flat()
    .forEach((message) => AlertStore.add(message));
};

export const errorHandler = (): AxiosBuilderInterceptor => ({
  type: 'response',
  errorHandler: (error: AxiosError) => {
    handleAxiosError(error);

    return Promise.reject(error);
  },
});
